import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * 2FA (HOTP via an authenticator app — Google Authenticator, Authy, 1Password,
 * etc.) setup/status/disable. All hit the legacy `/auth/2fa/*` controller.
 *
 * Enrollment flow (Settings → Security):
 *   1. useStartTwoFactorSetup() — mints a temp_token cookie for the current
 *      signed-in user (no body/response payload to read, just a side effect).
 *   2. useSetupTwoFactor() — generates a secret + QR code, shows it.
 *   3. User scans the QR in their authenticator app, types the 6-digit code
 *      it shows, and that's submitted via useAuth().verifyTwoFactor(code) —
 *      the SAME call used to complete a 2FA login challenge, since the
 *      backend treats "first successful code" as confirmation either way.
 *
 * Status query key is invalidated after both the enrollment-verify step and
 * disable, so the Settings card picks up the new on/off state.
 */

export interface TwoFactorSetup {
  secretKey: string;
  qrCodeUri: string;
  qrCodeBase64: string;
}

export const TWO_FACTOR_STATUS_KEY = ["two-factor-status"];

export function useTwoFactorStatus() {
  return useQuery({
    queryKey: TWO_FACTOR_STATUS_KEY,
    queryFn: ({ signal }) => api.get<{ enabled: boolean }>("/auth/2fa/status", signal)
  });
}

export function useStartTwoFactorSetup() {
  return useMutation({
    mutationFn: () => api.post<{ success: boolean }>("/auth/2fa/start-setup")
  });
}

export function useSetupTwoFactor() {
  return useMutation({
    mutationFn: () => api.post<TwoFactorSetup>("/auth/2fa/setup")
  });
}

export function useDisableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (currentPassword: string) =>
      api.post<{ success: boolean; message: string }>("/auth/2fa/disable", { currentPassword }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TWO_FACTOR_STATUS_KEY })
  });
}
